import { isPlatformWeb } from 'renative';

import { AnyPayload, AnalyticsClient, AnalyticsConfig, EventBody } from '@24i/nxg-sdk-photon';
import { ANALYTICS_TRIGGERS } from '@24i/nxg-sdk-smartott-shared/src/analytics/constants';
import { OneTrust } from '@24i/nxg-sdk-smartott-shared/src/utils/oneTrust';
import { OneTrustConsent } from '@24i/nxg-sdk-smartott-shared/src/utils/oneTrust/types';

type GTMConfigOptions = {
    enabled?: boolean;
    containerId?: string;
    lazyLoadEnabled?: boolean;
    dataMapper?: Partial<Record<ANALYTICS_TRIGGERS, string>>;
    customProperties?: Record<string, unknown>;
};

type GTMUserContext = {
    email?: string;
    userId?: string;
    [key: string]: unknown;
};

type GTMEventPayload = {
    event: string;
    triggerName: ANALYTICS_TRIGGERS;
    utm: Record<string, string>;
    user: GTMUserContext;
    privacy: OneTrustConsent;
    payload: Record<string, unknown>;
};

export type GTMScriptConfig = {
    enabled: boolean;
    containerId?: string;
};

type GlobalGtmBridge = {
    nxgTagManagerPush?: (eventPayload: GTMEventPayload) => void;
};

declare global {
    interface Window {
        dataLayer?: Array<Record<string, unknown>>;
    }
}

const GTM_NAME = 'GTM';

const GTM_DEFAULT_TRIGGERS: ANALYTICS_TRIGGERS[] = [
    ANALYTICS_TRIGGERS.SCENE_VIEW,
    ANALYTICS_TRIGGERS.PLAYBACK_START,
    ANALYTICS_TRIGGERS.PLAYBACK_PAUSE,
    ANALYTICS_TRIGGERS.PLAYBACK_UNPAUSE,
    ANALYTICS_TRIGGERS.PLAYBACK_STOP,
    ANALYTICS_TRIGGERS.LOGIN,
    ANALYTICS_TRIGGERS.LOGOUT,
    ANALYTICS_TRIGGERS.SIGN_UP,
    ANALYTICS_TRIGGERS.PURCHASE_START,
    ANALYTICS_TRIGGERS.PURCHASE_COMPLETE,
    ANALYTICS_TRIGGERS.PURCHASE_CANCEL,
    ANALYTICS_TRIGGERS.PLAYER_OPEN,
    ANALYTICS_TRIGGERS.PLAYER_CLOSE,
    ANALYTICS_TRIGGERS.AD_START,
    ANALYTICS_TRIGGERS.AD_SKIPPED,
    ANALYTICS_TRIGGERS.AD_BUFFERING,
    ANALYTICS_TRIGGERS.AD_ERROR,
    ANALYTICS_TRIGGERS.AD_STOP,
    ANALYTICS_TRIGGERS.AD_COMPLETE,
];

const DEFAULT_TRIGGER_TO_EVENT_NAME: Partial<Record<ANALYTICS_TRIGGERS, GTMEventPayload['event']>> =
    {
        [ANALYTICS_TRIGGERS.AD_BUFFERING]: 'ad_buffering',
        [ANALYTICS_TRIGGERS.AD_ERROR]: 'ad_error',
        [ANALYTICS_TRIGGERS.AD_SKIPPED]: 'ad_skipped',
        [ANALYTICS_TRIGGERS.AD_START]: 'ad_start',
        [ANALYTICS_TRIGGERS.AD_STOP]: 'ad_stop',
        [ANALYTICS_TRIGGERS.AD_COMPLETE]: 'ad_complete',
        [ANALYTICS_TRIGGERS.SCENE_VIEW]: 'scene_view',
        [ANALYTICS_TRIGGERS.PLAYBACK_START]: 'video_start',
        [ANALYTICS_TRIGGERS.PLAYBACK_PAUSE]: 'video_pause',
        [ANALYTICS_TRIGGERS.PLAYBACK_UNPAUSE]: 'video_unpause',
        [ANALYTICS_TRIGGERS.PLAYBACK_STOP]: 'video_stop',
        [ANALYTICS_TRIGGERS.PLAYER_OPEN]: 'player_open',
        [ANALYTICS_TRIGGERS.PLAYER_CLOSE]: 'player_close',
        [ANALYTICS_TRIGGERS.LOGIN]: 'customer_login',
        [ANALYTICS_TRIGGERS.LOGOUT]: 'customer_logout',
        [ANALYTICS_TRIGGERS.SIGN_UP]: 'customer_signup',
        [ANALYTICS_TRIGGERS.PURCHASE_START]: 'purchase_start',
        [ANALYTICS_TRIGGERS.PURCHASE_COMPLETE]: 'purchase_complete',
        [ANALYTICS_TRIGGERS.PURCHASE_CANCEL]: 'purchase_cancel',
    };

const FALLBACK_CONSENT: OneTrustConsent = {
    doNotTrack: null,
    limitAdvertisementTracking: null,
    tcfConsentString: null,
};

const getConfigOptions = (config: AnalyticsConfig): GTMConfigOptions =>
    (config.options ?? {}) as GTMConfigOptions;

const toDataMapper = (value: unknown): Partial<Record<ANALYTICS_TRIGGERS, string>> | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }

    const rawMapper = value as Record<string, unknown>;
    const dataMapper: Partial<Record<ANALYTICS_TRIGGERS, string>> = {};

    Object.keys(rawMapper).forEach((key) => {
        const mappedEventName = rawMapper[key];
        if (typeof mappedEventName !== 'string' || mappedEventName.length === 0) {
            return;
        }

        if (Object.values(ANALYTICS_TRIGGERS).includes(key as ANALYTICS_TRIGGERS)) {
            dataMapper[key as ANALYTICS_TRIGGERS] = mappedEventName;
        }
    });

    return Object.keys(dataMapper).length > 0 ? dataMapper : undefined;
};

export const extractUTMParams = (url?: string): Record<string, string> => {
    if (!url) return {};

    const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : url;
    const queryParams = new URLSearchParams(query);
    const utm: Record<string, string> = {};

    queryParams.forEach((value, key) => {
        if (key.startsWith('utm_') && value) {
            utm[key] = value;
        }
    });

    return utm;
};

const getCurrentWebUTM = (): Record<string, string> => {
    if (!isPlatformWeb || typeof window === 'undefined') return {};

    return extractUTMParams(window.location?.search);
};

const toRecord = (value: unknown): Record<string, unknown> => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    return {};
};

const userFromPayload = ({ user: rawUser }: AnyPayload): GTMUserContext => {
    const user = toRecord(rawUser);
    const { email, id, userId } = user;
    const resolvedUserId = id ?? userId;

    return {
        ...user,
        email: typeof email === 'string' ? email : undefined,
        userId:
            typeof resolvedUserId === 'string' || typeof resolvedUserId === 'number'
                ? String(resolvedUserId)
                : undefined,
    };
};

const campaignUrlFromPayload = (payload: AnyPayload): string | undefined => {
    if (typeof payload.campaignUrl === 'string' && payload.campaignUrl.length > 0) {
        return payload.campaignUrl;
    }

    const eventPayload = toRecord(payload.payload);
    const deepLinkUrl = eventPayload.url ?? eventPayload.deepLinkUrl;

    return typeof deepLinkUrl === 'string' ? deepLinkUrl : undefined;
};

export class GoogleTagManagerClient implements AnalyticsClient {
    name = GTM_NAME;

    triggers = GTM_DEFAULT_TRIGGERS;

    private triggerToEventName = DEFAULT_TRIGGER_TO_EVENT_NAME;

    private containerId: string | undefined;

    private enabled = false;

    private lazyLoadEnabled = true;

    private isLoaded = false;

    private queuedEvents: GTMEventPayload[] = [];

    private loadingPromise: Promise<void> | null = null;

    private scriptConfigListeners = new Set<(config: GTMScriptConfig) => void>();

    getScriptConfig(): GTMScriptConfig {
        return {
            enabled: this.enabled,
            containerId: this.containerId,
        };
    }

    subscribeScriptConfig(listener: (config: GTMScriptConfig) => void): () => void {
        this.scriptConfigListeners.add(listener);
        listener(this.getScriptConfig());

        return () => {
            this.scriptConfigListeners.delete(listener);
        };
    }

    private notifyScriptConfigChange() {
        const config = this.getScriptConfig();

        this.scriptConfigListeners.forEach((listener) => {
            listener(config);
        });
    }

    async init(config: AnalyticsConfig): Promise<void> {
        const options = getConfigOptions(config);

        const dataMapper = toDataMapper(options.dataMapper) ?? DEFAULT_TRIGGER_TO_EVENT_NAME;

        this.triggerToEventName = dataMapper;
        this.triggers = Object.keys(dataMapper) as ANALYTICS_TRIGGERS[];

        this.enabled = options.enabled ?? config.enabled;

        // Get container ID from config options, and ensure it's a non-empty string
        this.containerId =
            typeof options.containerId === 'string' && options.containerId.trim().length > 0
                ? options.containerId
                : undefined;

        // Lazy load is enabled by default, but can be disabled via remote config or local config for testing purposes
        const lazyLoadOption = options.lazyLoadEnabled;
        this.lazyLoadEnabled =
            typeof lazyLoadOption === 'boolean' ? lazyLoadOption : this.lazyLoadEnabled;

        // Disable the client if no containerId is provided – it cannot send events without one,
        // and leaving it enabled would cause queuedEvents to grow unboundedly.
        if (this.enabled && !this.containerId) {
            this.enabled = false;
            this.notifyScriptConfigChange();
            return;
        }

        if (!this.enabled || this.lazyLoadEnabled) {
            this.notifyScriptConfigChange();
            return;
        }

        await this.loadContainer();
        this.notifyScriptConfigChange();
    }

    async sendEvent(event: EventBody): Promise<void> {
        const eventPayload = event as GTMEventPayload;
        await this.ensureContainerLoaded();

        if (!this.isLoaded) {
            this.queuedEvents.push(eventPayload);
            return;
        }

        if (isPlatformWeb && typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(eventPayload);
            return;
        }

        const bridge = globalThis as typeof globalThis & GlobalGtmBridge;
        bridge.nxgTagManagerPush?.(eventPayload);
    }

    async onEvent(triggerName: ANALYTICS_TRIGGERS, payload: AnyPayload): Promise<void> {
        if (!this.enabled) return;

        const eventName = this.triggerToEventName[triggerName];
        if (!eventName) return;

        const payloadBody = toRecord(payload.payload);
        const campaignUrl = campaignUrlFromPayload(payload);
        const utmFromCampaign = extractUTMParams(campaignUrl);
        const utm = {
            ...getCurrentWebUTM(),
            ...utmFromCampaign,
        };

        const eventPayload: GTMEventPayload = {
            event: eventName,
            triggerName,
            utm,
            user: userFromPayload(payload),
            privacy: await this.getPrivacyConsent(),
            payload: payloadBody,
        };

        await this.sendEvent(eventPayload);
    }

    private async ensureContainerLoaded(): Promise<void> {
        if (this.isLoaded || !this.containerId) return;

        if (!this.loadingPromise) {
            this.loadingPromise = this.loadContainer().finally(() => {
                this.loadingPromise = null;
            });
        }

        await this.loadingPromise;

        if (this.isLoaded && this.queuedEvents.length > 0) {
            const queuedEvents = [...this.queuedEvents];
            this.queuedEvents = [];
            await Promise.all(queuedEvents.map((eventPayload) => this.sendEvent(eventPayload)));
        }
    }

    private async getPrivacyConsent(): Promise<OneTrustConsent> {
        try {
            return (await OneTrust?.getConsentStatus()) || FALLBACK_CONSENT;
        } catch {
            return FALLBACK_CONSENT;
        }
    }

    private async loadContainer(): Promise<void> {
        // If there is no container configured or it is already marked as loaded, nothing to do
        if (!this.containerId || this.isLoaded) return;

        // For non-web platforms we simply mark as loaded (native integrations use the bridge)
        if (!isPlatformWeb || typeof window === 'undefined') {
            this.isLoaded = true;
            return;
        }

        // On web we no longer inject a <script> tag directly from the client.
        // Script loading should be handled by a platform-specific React component that
        // uses `next/script` (web) or a native-safe no-op (native). Here we only
        // ensure the dataLayer is available for immediate pushes and mark the client
        // as loaded so sendEvent can push events into the dataLayer.
        try {
            // Guard access to window/dataLayer
            const webWindow = window as Window & { dataLayer?: Array<Record<string, unknown>> };
            webWindow.dataLayer = webWindow.dataLayer || [];
        } catch (_error: unknown) {
            // If window is unexpectedly unavailable, continue but don't throw.
        }

        this.isLoaded = true;
    }
}

export const createGoogleTagManagerClient = () => new GoogleTagManagerClient();

// Export a React component that is responsible for rendering the GTM <Script /> on web
// and a safe no-op on native. The platform-specific implementation lives in ./Script.{web,native}.tsx
export { default as GoogleTagManagerScript } from './Script';
